import React, { useMemo } from 'react';
import { Button, Dropdown, Form, Input, Select, Typography } from 'antd';
import { CopyOutlined, SkinOutlined } from '@ant-design/icons';
import DdnHeField from '../components/DdnHeField';
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import {
    FixedValueProvider, FormFieldPath, useFormPath,
    applyPersonCopy, buildPersonCopyValues, collectPersonBlocks,
} from './index';

const { Text } = Typography;

export default (formPlugin, namespace) => {

    return {
        Renderer: (props) => {
            const { listFieldProps, buildName, path, absolutePath, storePath } = useFormPath()

            if (props.remove) {
                formPlugin.onAddFormPlugin(props, props.nodeId, props.pluginConfig.id)
            }

            const { data } = props
            const reducedWidth = data.reduced ? "48%" : "100%"
            const finalName = "[CHILD]_" + data.fieldKey

            // Calculé une seule fois (data.fixedGenre stable durant un render)
            let _defaultValue = undefined
            let fixedGenre = false
            if (data.fixedGenre === "Homme" || data.fixedGenre === "Femme") {
                fixedGenre = true
                _defaultValue = data.fixedGenre === "Homme"
            }

            // On étend les DEUX chemins avec ce namespace enfant (même segment).
            // path → names Antd ; absolutePath → navigation formValues (conditions).
            const childContext = useMemo(() => ({
                path: [...path, finalName],
                absolutePath: [...absolutePath, finalName],
                storePath: [...storePath, finalName],
                listFieldProps: null,
            }), [path, absolutePath, storePath, finalName])

            // Chemin ABSOLU de CE bloc dans le store du Form racine : cible des
            // écritures « Copier depuis… » et exclusion de soi-même des candidats.
            const selfStorePath = childContext.storePath

            // Après TOUS les hooks (React exige un nombre de hooks constant entre deux
            // rendus ; en éditeur admin, isPreviewMode bascule sur la cellule montée).
            if ((props.isPreviewMode || props.readOnly) && !props.data.fieldKey) {
                return null
            }

            return (
                <div>
                    {/* « Copier depuis… » : préremplissage PONCTUEL depuis un autre bloc
                        enfant déjà saisi (autre produit, autre occurrence, bloc de
                        formulaire). Cas typique : le même enfant inscrit à deux sessions.
                        Les champs restent modifiables. `_form` = instance du Form RACINE
                        (chemins absolus). Masqué tant qu'aucun enfant n'est proposable. */}
                    <Form.Item noStyle shouldUpdate>
                        {(_form) => {
                            // Store RACINE lu par référence (aucun clone à chaque frappe) ;
                            // collectPersonBlocks écarte les occurrences au-delà de la quantité ;
                            // `disableCopyPerson` = interrupteur de l'org.
                            const others = data.disableCopyPerson ? [] : collectPersonBlocks(_form.getFieldsValue(true), "CHILD", selfStorePath)
                            if (!others.length) return null

                            const blockKey = (o) => "block:" + JSON.stringify(o.storePath)
                            const items = others.map(o => ({ key: blockKey(o), label: o.label }))

                            const onClick = ({ key }) => {
                                const entry = others.find(o => blockKey(o) === key)
                                // Entrée introuvable (liste re-rendue entre l'ouverture et le clic) :
                                // ne rien faire, surtout pas lire un chemin vide (= tout le store).
                                if (!entry) return
                                // Relecture au clic (jamais la valeur capturée au rendu).
                                const source = _form.getFieldValue(entry.storePath)
                                const values = buildPersonCopyValues(source, { fixedGenre })
                                applyPersonCopy(_form, formPlugin, selfStorePath, values, { keepKeys: fixedGenre ? ["homme"] : [] })
                            }

                            return (
                                <Form.Item>
                                    <Dropdown menu={{ items, onClick }} trigger={["click"]}>
                                        <Button size="small" icon={<CopyOutlined />}>{t`Copier depuis…`}</Button>
                                    </Dropdown>
                                </Form.Item>
                            )
                        }}
                    </Form.Item>

                    <Form.Item
                        {...(listFieldProps || {})}
                        label={t`Garçon / Fille` + " :"}
                        name={buildName([finalName, "homme"])}
                        rules={[{
                            required: data.required,
                            message: t`Ce champ est obligatoire`
                        }]}
                    >
                        <FixedValueProvider defaultValue={_defaultValue}>
                            <Select disabled={fixedGenre} size="large" placeholder={t`Garçon / Fille`}>
                                <Select.Option value={true}><Trans>Garçon</Trans></Select.Option>
                                <Select.Option value={false}><Trans>Fille</Trans></Select.Option>
                            </Select>
                        </FixedValueProvider>
                    </Form.Item>

                    <div style={{ display: data.reduced ? "flex" : "block", justifyContent: "space-between" }}>

                        <Form.Item
                            {...(listFieldProps || {})}
                            label={(data.labelPrenom || t`Prénom`) + " :"}
                            name={buildName([finalName, "prenom"])}
                            rules={[{
                                required: data.required,
                                message: t`Ce champ est obligatoire`
                            }]}
                            style={{ width: reducedWidth }}
                        >
                            <Input type="text" placeholder={data.labelPrenom || t`Prénom`} />
                        </Form.Item>

                        <Form.Item
                            {...(listFieldProps || {})}
                            label={(data.labelNom || t`Nom`) + " :"}
                            name={buildName([finalName, "nom"])}
                            rules={[{
                                required: data.required,
                                message: t`Ce champ est obligatoire`
                            }]}
                            style={{ width: reducedWidth }}
                        >
                            <Input type="text" placeholder={data.labelNom || t`Nom`} />
                        </Form.Item>

                    </div>

                    <div style={{
                        display: (data.reduced && props.device === "DESKTOP") ? "flex" : "block",
                        justifyContent: "space-between"
                    }}>
                        <Form.Item
                            {...(listFieldProps || {})}
                            label={(data.labelDdn || t`Date de naissance`) + " :"}
                            name={buildName([finalName, "ddn"])}
                            rules={[{
                                required: data.required,
                                message: t`Ce champ est obligatoire`
                            }]}
                            style={{ width: (data.reduced && props.device === "DESKTOP") ? reducedWidth : "100%" }}
                        >
                            <DdnHeField placeholder={data.labelDdn || t`Date de naissance`} />
                        </Form.Item>
                    </div>

                    <FormFieldPath.Provider value={childContext}>
                        {props.children}
                    </FormFieldPath.Provider>
                </div>
            )
        },
        namespace: namespace,
        id: 'form-child' + namespace,
        title: t`Champ Enfant`,
        description: t`Élément de formulaire utilisable.`,
        icon: <SkinOutlined />,
        iconColor: "#F39C12",
        version: 1,
        cellPlugins: (plugins, data) => {
            const initialPlugins = formPlugin.getPluginsMem(namespace).filter(i =>
                !i.id.includes("form-contact") &&
                !i.id.includes("form-child") &&
                !i.id.includes("form-hazkara") &&
                !i.id.includes("form-identity") &&
                !i.id.includes("form-product") &&
                !i.id.includes("form-step-container") &&
                !i.id.includes("form-submit-button") &&
                !i.id.includes("form-free-amount") &&
                !i.id.includes("form-paiement-method") &&
                !i.id.includes("form-calendar")
            )
            return [...initialPlugins, ...formPlugin.getContactFormPlugins(namespace)]
        },
        controls: {
            type: 'autoform',
            schema: {
                properties: {
                    required: {
                        type: 'boolean',
                        uniforms: {
                            label: t`Obligatoire`,
                        }
                    },
                    reduced: {
                        type: 'boolean',
                        uniforms: {
                            label: t`Formulaire Réduit`,
                        }
                    },
                    disableCopyPerson: {
                        type: 'boolean',
                        uniforms: { label: t`Désactiver la copie depuis une autre personne` }
                    },
                    fieldKey: {
                        type: 'string',
                        uniforms: {
                            required: true,
                            label: t`Identifiant`,
                            placeholder: t`Identifiant du Champ`,
                        }
                    },
                    labelNom: {
                        type: 'string',
                        uniforms: {
                            label: t`Étiquette Nom`,
                            placeholder: t`Étiquette Nom`,
                        }
                    },
                    labelPrenom: {
                        type: 'string',
                        uniforms: {
                            label: t`Étiquette Prénom`,
                            placeholder: t`Étiquette Prénom`,
                        }
                    },
                    labelDdn: {
                        type: 'string',
                        uniforms: {
                            label: t`Étiquette Date de naissance`,
                            placeholder: t`Étiquette Date de naissance`,
                        }
                    },
                    fixedGenre: {
                        type: 'string',
                        uniforms: {
                            label: t`Fixer le genre`,
                            allowedValues: ["Homme", "Femme"],
                            transform: (value) => {
                                if(value==="Homme"){
                                    return t`Garçon`
                                }else{
                                    return t`Fille`
                                }
                            },
                        }
                    },
                }
            },
        },
    }
}
